import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Layout, Space, Alert, Avatar, Spin, Result } from 'antd';
import AppHeader from '../components/AppHeader/AppHeader';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/api';
import { oauthService } from '../services/api';
import { SafetyOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;

const OAuthAuthorizePage = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [clientInfo, setClientInfo] = useState(null);
    const [authorizing, setAuthorizing] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Парсинг параметров из URL или состояния навигации
    const getQueryParams = () => {
        // Приоритет - параметры состояния навигации
        if (location.state?.oauthParams) {
            return location.state.oauthParams;
        }

        // Если нет состояния, парсим из URL
        const searchParams = new URLSearchParams(location.search);
        return {
            client_id: searchParams.get('client_id'),
            redirect_uri: searchParams.get('redirect_uri'),
            response_type: searchParams.get('response_type'),
            scope: searchParams.get('scope'),
            state: searchParams.get('state'),
            code_challenge: searchParams.get('code_challenge'),
            code_challenge_method: searchParams.get('code_challenge_method'),
            auth_request_id: searchParams.get('auth_request_id')
        };
    };

    const params = getQueryParams();

    // Функция проверки и сохранения авторизации
    const checkAuth = async () => {
        setLoading(true);
        try {
            // Проверяем, аутентифицирован ли пользователь
            if (!authService.isAuthenticated()) {
                // Если пользователь не аутентифицирован, сохраняем параметры и перенаправляем на страницу входа
                localStorage.setItem('oauth_params', JSON.stringify(params));
                navigate('/login');
                return;
            }

            // Проверяем, есть ли у пользователя временный пароль
            if (authService.hasTemporaryPassword()) {
                // Если у пользователя временный пароль, сохраняем параметры и перенаправляем
                localStorage.setItem('oauth_params', JSON.stringify(params));
                navigate('/change-temporary-password');
                return;
            }

            // Получаем информацию о клиенте
            const clientResponse = await oauthService.getClientInfo(params.client_id, params.redirect_uri);

            if (clientResponse.status === 'success' && clientResponse.data.validated) {
                setClientInfo(clientResponse.data);
                setError(null);
            } else {
                setError({
                    statusCode: 400,
                    message: clientResponse.message || 'Неверный клиент или redirect_uri. Закройте окно и повторите авторизацию ещё раз.'
                });
            }
        } catch (err) {
            console.error('Ошибка при проверке аутентификации:', err);
            setError({
                statusCode: 500,
                message: 'Произошла ошибка при проверке аутентификации. Пожалуйста, попробуйте снова.'
            });
        } finally {
            setLoading(false);
        }
    };

    // Загрузка данных при первом рендере
    useEffect(() => {
        // Проверяем наличие необходимых параметров
        if (!params.client_id || !params.redirect_uri || !params.response_type) {
            setError({
                statusCode: 400,
                message: 'Отсутствуют обязательные параметры запроса OAuth.'
            });
            setLoading(false);
            return;
        }

        // После возврата с логина или изменения пароля
        const storedParams = localStorage.getItem('oauth_params');
        if (storedParams) {
            localStorage.removeItem('oauth_params');
        }

        checkAuth();
    }, [location.search, location.state]);

    // Обработчик подтверждения авторизации
    const handleAuthorize = async () => {
        setAuthorizing(true);
        try {
            // Отправляем запрос на авторизацию
            const authorizeResponse = await oauthService.authorize({
                client_id: params.client_id,
                redirect_uri: params.redirect_uri,
                response_type: params.response_type,
                scope: params.scope,
                state: params.state,
                code_challenge: params.code_challenge,
                code_challenge_method: params.code_challenge_method,
                auth_request_id: params.auth_request_id
            });

            if (authorizeResponse.status === 'success' && authorizeResponse.redirect_info.should_redirect) {
                // Перенаправляем пользователя по URL, полученному от сервера
                window.location.href = authorizeResponse.redirect_info.redirect_url;
            } else {
                setError({
                    statusCode: 400,
                    message: authorizeResponse.message || 'Ошибка при авторизации. Пожалуйста, попробуйте снова.'
                });
            }
        } catch (err) {
            console.error('Ошибка при авторизации:', err);
            setError({
                statusCode: 500,
                message: 'Произошла ошибка при авторизации. Пожалуйста, попробуйте снова.'
            });
        } finally {
            setAuthorizing(false);
        }
    };

    // Функция отображения контента
    const renderContent = () => {
        // Отображение загрузки
        if (loading) {
            return (
                <Card variant="bordered" style={{ width: '100%', maxWidth: 400 }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px' }}>
                        <Space direction="vertical" align="center">
                            <Spin size="large" />
                            <Text>Загрузка...</Text>
                        </Space>
                    </div>
                </Card>
            );
        }

        // Отображение ошибки
        if (error) {
            return (
                <Card variant="bordered" style={{ width: '100%', maxWidth: 400 }}>
                    <Result
                        status="error"
                        title={error.statusCode || "Ошибка"}
                        subTitle={error.message}
                        extra={[
                            <Button type="primary" key="back" onClick={() => navigate('/login')}>
                                Вернуться на страницу входа
                            </Button>
                        ]}
                    />
                </Card>
            );
        }

        // Отображение страницы подтверждения
        return (
            <Card variant="bordered" style={{ width: '100%', maxWidth: 400 }}>
                <Space direction="vertical" align="center" style={{ width: '100%', textAlign: 'center' }}>
                    <Avatar size={64} icon={<SafetyOutlined />} style={{ backgroundColor: '#05D32A' }} />
                    <Title level={4}>Вход в {clientInfo?.name}</Title>
                    <Paragraph>
                        {clientInfo?.name} получит доступ к вашим данным
                    </Paragraph>
                    <Button
                        type="primary"
                        size="large"
                        block
                        onClick={handleAuthorize}
                        loading={authorizing}
                    >
                        Продолжить
                    </Button>
                </Space>
            </Card>
        );
    };

    // Отображение страницы
    return (
        <Layout>
            <AppHeader />
            <Content>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh', // Вычитаем высоту AppHeader
                        padding: '24px'
                    }}
                >
                    {renderContent()}
                </div>
            </Content>
        </Layout>
    );
};

export default OAuthAuthorizePage;